import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useWebViewStore } from '../webview/useWebViewStore';

const ROOT = "/unify";

export type RouteContext =
  | { kind: "home" ,context:"unify"}
  | { kind: "myUnify" ,context:"unify"}
  | { kind: "workflows" ,context:"unify"}
  | { kind: "applications" ,context:"unify"}
  | { kind: "dealerAdmin" ,context:"unify"}
  | { kind: "unifyAdmin" ,context:"unify"}
  | { kind: "dashboards" ,context:"unify"}
  | { kind: "customView"; id: string ,context:string}
  | { kind: "appView"; appId: string; fromUniversity: string,context:string }
  | { kind: "workflowView"; appId: string; workflowId: string; fromUniversity: string,context:string }
  | { kind: "docs" ,context:"unify" }
  | { kind: "customerCare" ,context:"unify" }
  | { kind: "globalSearch" ,context:"global-search" }
  | { kind: "globalSearchHistory",context:"global-search" }
  | { kind: "unknown"; path: string };

export function getRouteContext(pathname: string): RouteContext {
  // Normalize
  if (!pathname.startsWith(ROOT)) {
    return { kind: "unknown", path: pathname };
  }

  const rest = pathname.slice(ROOT.length);

  // Simple routes
  if (rest === "/home") return { context:"unify", kind: "home" };
  if (rest === "/myUnify") return { context:"unify", kind: "myUnify" };
  if (rest === "/workflows") return { context:"unify", kind: "workflows" };
  if (rest === "/dashboards") return { context:"unify", kind: "dashboards" };
  if (rest === "/applications") return { context:"unify", kind: "applications" };
  if (rest === "/dealer-admin") return { context:"unify", kind: "dealerAdmin" };
  if (rest === "/unify-admin") return { context:"unify", kind: "unifyAdmin" };
  if (rest === "/docs") return { context:"unify", kind: "docs" };
  if (rest === "/customerCare") return { context:"unify", kind: "customerCare" };
  if (rest === "/global-search") return { context:"global-search", kind: "globalSearch" };
  if (rest === "/global-search-history") return { context:"global-search", kind: "globalSearchHistory" };
  // /view/:id/cta
  {
    const m = rest.match(/^\/view\/([^/]+)\/cta$/);
    if (m) return { kind: "customView", id: m[1], context:m[1], };
  }
  {
    const m = rest.match(/^\/aview\/([^/]+)\/([^/]+)$/);
    if (m) {
      const [, appId, fromUniversity] = m;
      return { kind: "appView", appId, fromUniversity,context:appId };
    }
  }
  {
    const m = rest.match(/^\/wview\/([^/]+)\/([^/]+)\/([^/]+)$/);
    if (m) {
      const [, appId, workflowId, fromUniversity] = m;
      return { kind: "workflowView", appId, workflowId, fromUniversity ,context:workflowId};
    }
  }

  return { kind: "unknown", path: pathname };
}

export const IFRAME_ROUTE_KINDS = new Set(['appView', 'workflowView', 'customView', 'unknown']);


export default function BackForwardListener() {
  const history = useHistory();
  const location = useLocation();
  const { resetActive,setIndex } = useWebViewStore();

  const nav = window?.navigation;

  // Every fresh document load (including the redirect back from Okta login)
  // resets the Navigation API entry list, so the page we land on is always
  // index 0. If the user then hits browser Back, they'd fall out of this
  // document entirely and back into the Okta login flow behind it — a
  // navigation we have no way to intercept once it starts. So instead we
  // push a duplicate "guard" entry right away, making index 0 a same-document
  // entry we control. Any traverse back onto that guard is bounced forward
  // again, trapping Back within the app instead of letting it escape to login.
  useEffect(() => {
    if (!nav) return;
    if (nav.currentEntry?.index === 0) {
      history.push(location.pathname + location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!nav) return;

    const onEvent = (event: any) => {
      const index = nav.currentEntry?.index;

      if (index === 0 && event.navigationType === 'traverse') {
        nav.forward();
      }
    };
    nav.addEventListener('currententrychange', onEvent);
    return () => nav.removeEventListener('currententrychange', onEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const ctx = getRouteContext(location.pathname);
    if (!IFRAME_ROUTE_KINDS.has(ctx.kind)) {
      const { webviews } = useWebViewStore.getState();
         
      if (webviews.some(wv => wv.active)) {
        console.log('[cm] shell route active — resetting webview:', location.pathname);

        resetActive();
        history.replace(location.pathname);
        const wv = webviews.find(wv => wv.active);
        if(wv){
          setIndex(wv.id, -1);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
}
